import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { setPageTitle } from "../../../Store/modules/mms/actions";
import MmsApiService from "Services/MMS/mms-api.service";
import CreateEditAssetTypeModal from "Components/modals/AssetTypeModal/create-edit-asset-type-modal";
import DeleteItemModal from "Components/modals/delete-item-modal/delete-item-modal.component";
import {
  IconButton,
  MenuItem,
  Popover,
  CircularProgress,
  Fade,
} from "@material-ui/core";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import AlertToastr from "utils/alert";

const AssetTypes = (props) => {
  const { setPageTitle } = props;
  const history = useHistory();
  const [assetTypes, setAssetTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedAssetType, setSelectedAssetType] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingAssetType, setEditingAssetType] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [assetTypeToDelete, setAssetTypeToDelete] = useState(null);

  const createCombinedData = (assetTypes) => {
    const combinedData = [];

    assetTypes.forEach((assetType) => {
      assetType.componentTypes?.forEach((componentType) => {
        componentType.manufacturers?.forEach((manufacturer) => {
          manufacturer.models?.forEach((model) => {
            combinedData.push({
              id: `${assetType.id}-${componentType.id}-${manufacturer.id}-${model.id}`,
              assetType: assetType.name,
              componentType: componentType.name,
              manufacturer: manufacturer.name,
              model: model.name,
              assetTypeId: assetType.id,
              componentTypeId: componentType.id,
              manufacturerId: manufacturer.id,
              modelId: model.id,
              metrics: componentType.metrics || [],
              data: {
                assetType: assetType,
                componentType: componentType,
                manufacturer: manufacturer,
                model: model,
              },
            });
          });
        });
      });
    });

    return combinedData;
  };

  const calculateRowspans = (data) => {
    const processedData = [];

    for (let i = 0; i < data.length; i++) {
      const current = data[i];
      const processed = { ...current };

      if (i === 0 || data[i - 1].assetTypeId !== current.assetTypeId) {
        processed.assetTypeRowspan = 1;
        for (
          let j = i + 1;
          j < data.length && data[j].assetTypeId === current.assetTypeId;
          j++
        ) {
          processed.assetTypeRowspan++;
        }
      } else {
        processed.assetTypeRowspan = 0;
      }

      if (
        i === 0 ||
        data[i - 1].assetTypeId !== current.assetTypeId ||
        data[i - 1].componentTypeId !== current.componentTypeId
      ) {
        processed.componentTypeRowspan = 1;
        for (
          let j = i + 1;
          j < data.length &&
          data[j].assetTypeId === current.assetTypeId &&
          data[j].componentTypeId === current.componentTypeId;
          j++
        ) {
          processed.componentTypeRowspan++;
        }
      } else {
        processed.componentTypeRowspan = 0;
      }

      if (
        i === 0 ||
        data[i - 1].assetTypeId !== current.assetTypeId ||
        data[i - 1].componentTypeId !== current.componentTypeId ||
        data[i - 1].manufacturerId !== current.manufacturerId
      ) {
        processed.manufacturerRowspan = 1;
        for (
          let j = i + 1;
          j < data.length &&
          data[j].assetTypeId === current.assetTypeId &&
          data[j].componentTypeId === current.componentTypeId &&
          data[j].manufacturerId === current.manufacturerId;
          j++
        ) {
          processed.manufacturerRowspan++;
        }
      } else {
        processed.manufacturerRowspan = 0;
      }

      processedData.push(processed);
    }

    return processedData;
  };

  const fetchAssetTypes = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await MmsApiService.getAssetTypes();

      const combinedData = createCombinedData(response.data || []);
      const processedData = calculateRowspans(combinedData);
      setAssetTypes(processedData);
    } catch (error) {
      console.error("Error fetching asset types:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    setPageTitle("Asset Types");
    fetchAssetTypes();
  }, [setPageTitle, fetchAssetTypes]);

  useEffect(() => {
    const handleRefreshAssetTypes = () => {
      setTimeout(() => {
        fetchAssetTypes();
      }, 1000);
    };

    window.addEventListener("refreshAssetTypes", handleRefreshAssetTypes);

    return () => {
      window.removeEventListener("refreshAssetTypes", handleRefreshAssetTypes);
    };
  }, [fetchAssetTypes]);

  const handleMenuClick = (event, item) => {
    event.preventDefault();
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedAssetType(item);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedAssetType(null);
  };

  const handleEdit = async () => {
    try {
      if (selectedAssetType?.data?.assetType?.id) {
        const response = await MmsApiService.getAssetType(
          selectedAssetType.data.assetType.id
        );
        setEditingAssetType(response.data);
        setIsEditModalOpen(true);
      }
    } catch (error) {
      console.error("Error fetching asset type for editing:", error);
      AlertToastr.showErrorAlert("Error loading asset type data for editing");
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (selectedAssetType?.data?.assetType) {
      setAssetTypeToDelete(selectedAssetType.data.assetType);
      setIsDeleteModalOpen(true);
    }
    handleMenuClose();
  };

  const handleAssetTypeClick = (assetTypeId) => {
    history.push(`/asset-types/${assetTypeId}`);
  };


  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setEditingAssetType(null);
  };

  const handleEditSuccess = () => {
    handleEditModalClose();
    setTimeout(() => {
      fetchAssetTypes();
    }, 1000);
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
    setAssetTypeToDelete(null);
  };

  const handleConfirmDelete = async () => {
    try {
      if (assetTypeToDelete?.id) {
        await MmsApiService.deleteAssetType(assetTypeToDelete.id);

        setTimeout(() => {
          fetchAssetTypes();
        }, 1000);
        AlertToastr.showAlert("Asset type deleted successfully");
      }
    } catch (error) {
      console.error("Error deleting asset type:", error);
      AlertToastr.showErrorAlert(
        "Error deleting asset type. Please try again."
      );
    } finally {
      handleDeleteModalClose();
    }
  };

  return (
    <>
      <div className="pmPageHolder">
        <div className="pmPageBlock">
          <div className="pmPageBlockTitle">Asset Types</div>
          <div className="pmTableHolder" style={{ marginTop: "50px" }}>
            <table className="pmTable assetTypesTable">
              <thead>
                <tr>
                  <th>Asset Type</th>
                  <th>Component Type</th>
                  <th>Manufacturer</th>
                  <th>Model</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {assetTypes.map((item, index) => (
                  <tr key={item.id}>
                    {item.assetTypeRowspan > 0 && (
                      <td
                        className="text-semi-bold"
                        rowSpan={item.assetTypeRowspan}
                        style={{ 
                          verticalAlign: "top",
                          cursor: "pointer",
                          color: "#006ae7"
                        }}
                        onClick={() => handleAssetTypeClick(item.assetTypeId)}
                      >
                        {item.assetType}
                      </td>
                    )}
                    {item.componentTypeRowspan > 0 && (
                      <td
                        rowSpan={item.componentTypeRowspan}
                        style={{
                          verticalAlign: "top",
                          borderLeft: "0.5px solid #e0e0e0",
                          paddingLeft: "10px",
                        }}
                      >
                        <div>{item.componentType}</div>
                        {item.metrics && item.metrics.length > 0 && (
                          <div style={{ marginTop: "8px" }}>
                            <div style={{ fontSize: "12px" }}>Metrics:</div>
                            {item.metrics.map((metric) => {
                              const displayText = metric ? metric.name : '';
                              return displayText ? (
                                <li 
                                  key={metric.id}
                                  style={{ 
                                    fontSize: "10px", 
                                    color: "#666",
                                    marginBottom: "4px",
                                    lineHeight: "1.4",
                                    marginLeft: "30px"
                                  }}
                                >
                                  {displayText}
                                </li>
                              ) : null;
                            })}
                          </div>
                        )}
                      </td>
                    )}
                    {item.manufacturerRowspan > 0 && (
                      <td
                        rowSpan={item.manufacturerRowspan}
                        style={{
                          verticalAlign: "top",
                          borderLeft: "0.5px solid #e0e0e0",
                          paddingLeft: "10px",
                        }}
                      >
                        {item.manufacturer}
                      </td>
                    )}
                    <td
                      style={{
                        verticalAlign: "top",
                        borderLeft: "0.5px solid #e0e0e0",
                        paddingLeft: "10px",
                      }}
                    >
                      {item.model}
                    </td>
                    <td className="btnCell">
                      {item.assetTypeRowspan > 0 && (
                        <IconButton
                          aria-label="more"
                          size="small"
                          className="dropDownBtn"
                          onClick={(e) => handleMenuClick(e, item)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {isLoading && (
              <div className="loaderHolder">
                <Fade in={isLoading} unmountOnExit>
                  <CircularProgress />
                </Fade>
              </div>
            )}

            {!isLoading && assetTypes.length === 0 && (
              <div className="emptyInfoHolder">
                <p className="textGray text-center">No Asset Types found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <Popover
        anchorEl={menuAnchorEl}
        keepMounted
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        disableScrollLock={true}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        className="pmPopover equipmentSubMenu"
      >
        <MenuItem key="edit" onClick={handleEdit} className="menuItem edit">
          Edit
        </MenuItem>
        <MenuItem
          key="delete"
          onClick={handleDelete}
          className="menuItem delete"
        >
          Delete
        </MenuItem>
      </Popover>

      <CreateEditAssetTypeModal
        isOpen={isEditModalOpen}
        isOpenChangeHandler={handleEditModalClose}
        editingData={editingAssetType}
        onSuccess={handleEditSuccess}
      />

      <DeleteItemModal
        isOpen={isDeleteModalOpen}
        isOpenManager={setIsDeleteModalOpen}
        message={`Are you sure you want to delete Asset Type "${assetTypeToDelete?.name}"?`}
        onDeleteModalClose={handleConfirmDelete}
      />
    </>
  );
};

const mapStateToProps = ({ mms }) => ({
  currentDepartment: mms.currentDepartment,
});

const mapDispatchToProps = {
  setPageTitle,
};

export default connect(mapStateToProps, mapDispatchToProps)(AssetTypes);
